import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * 공통 팝업 래퍼 컴포넌트
 * 드래그 앤 드롭, 닫기 버튼, 레이아웃 등 공통 기능을 제공
 */
const PopupWrapper = ({
  children,
  config,
  onClose,
  displayType = 'modal',
  className = '',
  id = null
}) => {
  const { t } = useTranslation();
  const popupContentRef = useRef(null);
  const [popupPosition, setPopupPosition] = useState({ top: null, left: null });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const canDrag = displayType === 'modal';
  const closeOnBackdropSlideRight = displayType === 'slideRight';

  // 팝업 크기 스타일 계산
  const getSizeStyle = () => {
    const sizeStyle = {};

    if (config?.width) {
      sizeStyle.width = typeof config.width === 'number' ? `${config.width}px` : config.width;
    }
    if (config?.height) {
      sizeStyle.height = typeof config.height === 'number' ? `${config.height}px` : config.height;
    }
    if (config?.maxWidth) {
      sizeStyle.maxWidth = typeof config.maxWidth === 'number' ? `${config.maxWidth}px` : config.maxWidth;
    }
    if (config?.maxHeight) {
      sizeStyle.maxHeight = typeof config.maxHeight === 'number' ? `${config.maxHeight}px` : config.maxHeight;
    }
    if (config?.minWidth) {
      sizeStyle.minWidth = typeof config.minWidth === 'number' ? `${config.minWidth}px` : config.minWidth;
    }
    if (config?.minHeight) {
      sizeStyle.minHeight = typeof config.minHeight === 'number' ? `${config.minHeight}px` : config.minHeight;
    }

    return sizeStyle;
  };

  const contentStyle = {
    ...(canDrag && popupPosition.top !== null && popupPosition.left !== null
      ? { top: `${popupPosition.top}px`, left: `${popupPosition.left}px`, right: 'auto' }
      : {}),
    ...getSizeStyle()
  };

  // 팝업이 열릴 때 위치 초기화
  useEffect(() => {
    setPopupPosition({ top: null, left: null });
    setIsDragging(false);
    setDragOffset({ x: 0, y: 0 });
  }, [config]);

  // 드래그 시작 핸들러
  const handleDragStart = useCallback((e) => {
    if (!canDrag) return;

    if (popupContentRef.current) {
      const rect = popupContentRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
      e.preventDefault();
      e.stopPropagation();
    }
  }, [canDrag]);

  // 드래그 중 핸들러
  const handleDrag = useCallback((e) => {
    if (!isDragging || !canDrag) return;
    if (!popupContentRef.current) return;

    e.preventDefault();

    let newLeft = e.clientX - dragOffset.x;
    let newTop = e.clientY - dragOffset.y;

    const rect = popupContentRef.current.getBoundingClientRect();
    const maxX = window.innerWidth - rect.width;
    const maxY = window.innerHeight - rect.height;

    if (newLeft < 0) newLeft = 0;
    if (newLeft > maxX) newLeft = maxX;
    if (newTop < 0) newTop = 0;
    if (newTop > maxY) newTop = maxY;

    setPopupPosition({ top: newTop, left: newLeft });
  }, [isDragging, dragOffset, canDrag]);

  // 드래그 종료 핸들러
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // 드래그 이벤트 리스너 등록/해제
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDrag);
      document.addEventListener('mouseup', handleDragEnd);
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleDrag);
      document.removeEventListener('mouseup', handleDragEnd);
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleDrag, handleDragEnd]);

  // 배경 클릭 핸들러
  const handleBackdropClick = useCallback((e) => {
    if (isDragging) return;
    const closeOnBackdrop = config?.closeOnBackdrop === true || closeOnBackdropSlideRight;
    if (e.target === e.currentTarget && closeOnBackdrop) {
      onClose(false);
    }
  }, [onClose, isDragging, config, closeOnBackdropSlideRight]);

  // 팝업 컨테이너 클래스명
  const getContainerClassName = () => {
    if (displayType === 'slide') return 'popupContainer popupContainer-slide';
    if (displayType === 'slideRight') return 'popupContainer popupContainer-slideRight';
    return 'popupContainer';
  };

  // 팝업 콘텐츠 클래스명
  const getContentClassName = (baseClass) => {
    if (displayType === 'slide') return `${baseClass} popupContent-slide`;
    if (displayType === 'slideRight') return `${baseClass} popupContent-slideRight`;
    return baseClass;
  };

  return (
    <div className={getContainerClassName()} onClick={handleBackdropClick}>
      <div
        ref={popupContentRef}
        id={id}
        className={`popupContent ${className} ${getContentClassName('')} ${canDrag ? 'popup-draggable' : ''} ${isDragging ? 'dragging' : ''}`}
        style={contentStyle}
      >
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a
          className="popup-close"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            onClose(false);
          }}
        >
          <i className="pe-7s-close"></i>
        </a>
        <div className="container">
          <div className="main-popup-title">
            <div className="padding-10">
              <h3
                className={canDrag ? 'popup-drag-handle' : ''}
                onMouseDown={canDrag ? handleDragStart : undefined}
              >
                {config?.title || t('components.common.alert')}
              </h3>
            </div>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

export default PopupWrapper;

